// App.tsx — RouteX.dev Admin
import 'reflect-metadata'
import '@mantine/core/styles.css'
import '@mantine/dates/styles.css'
import '@mantine/notifications/styles.css'
import '@mantine/spotlight/styles.css'
import '@archbase/components/dist/index.css'
import '@archbase/layout/dist/index.css'
import '@archbase/admin/dist/index.css'

import {
  ActionIcon,
  Menu,
  Tooltip,
  Badge,
  Text,
  Flex,
  Group,
  useMantineColorScheme,
  MantineThemeOverride,
  Stack,
  Image,
  Indicator,
  Avatar,
  LoadingOverlay,
} from '@mantine/core'
import { useFullscreenDocument, useHotkeys, useLocalStorage, useMediaQuery } from '@mantine/hooks'
import { Dispatch, ErrorInfo, Fragment, ReactNode, SetStateAction, useEffect, useState, useCallback } from 'react'
import {
  IconArrowsMaximize,
  IconBrandMessenger,
  IconLogout,
  IconMoonStars,
  IconSun,
  IconUserCircle,
  IconLayoutSidebar,
  IconLayoutSidebarRight,
  IconLayoutList,
} from '@tabler/icons-react'
import { Login } from '@views/login/Login'
import translation_en from '@locales/en/translation.json'
import translation_ptbr from '@locales/pt-BR/translation.json'
import translation_es from '@locales/es/translation.json'
import containerIOC from '@ioc/ContainerIOC'
import { AppThemeDark, AppThemeLight, RoutexTokens } from '@theme/index'
import { logo } from '@assets/logo'
import { navigationData } from './navigation/navigationData'
import { ErrorFallback } from '@utils/ErrorFallback'
import { AppUser } from '@auth/AppAuthenticator'
import { clearCurrentUser, persistCurrentUser } from '@auth/currentUser'
import { APP_NAME, APP_VERSION, TRANSLATION_NAME } from './AppConstants'

import {
  useArchbaseTheme,
  processErrorMessage,
  ArchbaseAppProvider,
  ArchbaseGlobalProvider,
  ArchbaseErrorBoundary,
  ARCHBASE_IOC_API_TYPE,
  archbaseI18next,
} from '@archbase/core'
import { useArchbaseRemoteServiceApi, useArchbaseStore } from '@archbase/data'
import { ArchbaseUserService, useArchbaseAuthenticationManager, ArchbaseSecurityProvider } from '@archbase/security'
import {
  defaultAvatar,
  useArchbaseAdminStore,
  ArchbaseAdminLayoutHeader,
  ArchbaseAdminMainLayout,
  ArchbaseAdminTabContainer,
  ArchbaseTabItem,
  CommandPaletteButton,
  ArchbaseMyProfileModal,
  ArchbaseNavigationProvider,
} from '@archbase/admin'

type MainProps = {
  onLoginUser: (user: AppUser) => void
  onLogoutUser: () => void
  user?: AppUser | undefined
  setUser: Dispatch<SetStateAction<AppUser | undefined>>
}

function Main({ onLoginUser, onLogoutUser, user, setUser }: MainProps) {
  const { toggle } = useFullscreenDocument()
  const theme = useArchbaseTheme()
  const isMedium = useMediaQuery(`(max-width: ${theme.breakpoints.md})`)
  const { colorScheme, toggleColorScheme } = useMantineColorScheme()
  const adminStore = useArchbaseAdminStore()
  const templateStore = useArchbaseStore()
  const [isCollapsed, setCollapsed] = useState<boolean>(false)
  const [isHidden, setHidden] = useState<boolean>(false)
  const [sidebarVariant, setSidebarVariant] = useLocalStorage<'standard' | 'rail' | 'minimal'>({
    key: 'routex-sidebar-variant',
    defaultValue: 'rail',
  })
  const isCompact = isCollapsed || sidebarVariant === 'minimal'
  const [isGettingUser, setIsGettingUser] = useState<boolean>(false)
  const [userInfoLoaded, setUserInfoLoaded] = useState<boolean>(false)
  const [showMyProfile, setShowMyProfile] = useState<boolean>(false)
  const usuarioServiceApi = useArchbaseRemoteServiceApi<ArchbaseUserService>(ARCHBASE_IOC_API_TYPE.User)
  const api = import.meta.env.VITE_API || (typeof window !== 'undefined' ? window.location.origin : '')
  const isDev = api.includes('localhost') || api.includes('192.168.')

  const { login, loginWithContext, logout, username, isAuthenticated, isAuthenticating, isInitializing, error, accessToken, context } =
    useArchbaseAuthenticationManager({})

  const getUserInfo = useCallback(async () => {
    if (!accessToken || isGettingUser || userInfoLoaded) return

    try {
      setIsGettingUser(true)
      if (!username || username.trim() === '') {
        await logout()
        return
      }

      if (context && typeof context === 'object' && 'userId' in context) {
        const ctx = context as any
        onLoginUser(
          new AppUser({
            id: ctx.userId || username,
            displayName: ctx.fullName || username,
            email: ctx.email || username,
            photo: ctx.avatarUrl,
            isAdmin: ctx.role === 'ADMIN',
            role: ctx.role || 'ADMIN',
            permissions: ctx.permissions || [],
            isActive: true,
          })
        )
        setUserInfoLoaded(true)
      } else {
        try {
          const usuario = await usuarioServiceApi.getUserByEmail(username)
          onLoginUser(
            new AppUser({
              id: usuario.id,
              displayName: usuario.name,
              email: usuario.email,
              photo: usuario.avatar && usuario.avatar !== '' ? atob(usuario.avatar) : undefined,
              isAdmin: usuario.isAdministrator || false,
              role: usuario.isAdministrator ? 'ADMIN' : 'USER',
              permissions: [],
              isActive: !usuario.accountDeactivated,
            })
          )
          setUserInfoLoaded(true)
        } catch {
          onLoginUser(
            new AppUser({
              id: username,
              displayName: username.split('@')[0],
              email: username,
              isAdmin: true,
              role: 'ADMIN',
              permissions: [],
              isActive: true,
            })
          )
          setUserInfoLoaded(true)
        }
      }
    } catch (err: any) {
      const errMsg = processErrorMessage(err)
      if (errMsg.includes('401') || err?.response?.status === 403) {
        logout()
        onLogoutUser()
      }
    } finally {
      setIsGettingUser(false)
    }
  }, [accessToken, username, context, usuarioServiceApi, isGettingUser, userInfoLoaded, onLoginUser, logout, onLogoutUser])

  useEffect(() => {
    if (isAuthenticated && accessToken) {
      getUserInfo().catch(() => {})
    }
  }, [isAuthenticated, accessToken, getUserInfo])

  const handleLogin = async (email: string, password: string, rememberMe: boolean) => {
    if (loginWithContext) {
      await loginWithContext({ email, password, context: 'WEB_ADMIN' }, rememberMe)
    } else {
      login(email, password, rememberMe)
    }
  }

  const handleLogout = () => {
    templateStore.reset()
    setUserInfoLoaded(false)
    clearCurrentUser()
    logout()
    onLogoutUser()
  }

  const handleOpenMyProfileModal = () => setShowMyProfile(true)
  const handleCloseMyProfileModal = () => setShowMyProfile(false)

  const handleUpdateUser = (newName: string, newAvatar?: string) => {
    setUser((prev) => {
      if (!prev) return prev
      return new AppUser({
        id: prev.id,
        displayName: newName ?? prev.displayName,
        email: prev.email,
        photo: newAvatar ? atob(newAvatar) : prev.photo,
        isAdmin: prev.isAdmin,
        role: prev.role,
        permissions: prev.permissions,
        isActive: prev.isActive,
      })
    })
  }

  const headerActions = (): ReactNode[] => {
    const result: ReactNode[] = []
    if (isDev) {
      result.push(
        // Gradiente da paleta, nao azul cravado — outro ponto que a troca de tema nao alcancava.
        <Badge
          key="dev"
          size="lg"
          variant="gradient"
          gradient={{ from: RoutexTokens.amber500, to: RoutexTokens.gold, deg: 90 }}
        >
          DEV
        </Badge>
      )
    }
    result.push(
      <Tooltip key="fullscreen" withinPortal withArrow label="Tela cheia">
        <ActionIcon variant="transparent" c="#868E96" onClick={toggle}>
          <IconArrowsMaximize size="1.5rem" />
        </ActionIcon>
      </Tooltip>
    )
    return result
  }

  const logError = (error: Error, info: ErrorInfo) => {
    console.error(error, info)
  }

  const loginView = <Login onLogin={handleLogin} error={error} />

  const isLoading = isInitializing || isGettingUser || isAuthenticating
  if (isLoading) return <LoadingOverlay visible={isLoading} />

  return (
    <ArchbaseErrorBoundary FallbackComponent={ErrorFallback} onError={logError}>
      {isAuthenticated && user ? (
        <Fragment>
          <ArchbaseAdminMainLayout
            sideBarCollapsedSubmenuWidth={300}
            sideBarTextDarkColor="white"
            sideBarTextLightColor="white"
            // Icones em bege acinzentado, nao na primaria: o ambar vivo sobre o marrom escuro
            // fica neon e competiria com o item ativo. O rotulo segue branco, entao a
            // hierarquia texto > icone se mantem.
            sideBarIconDarkColor={RoutexTokens.sidebarIcon}
            sideBarIconLightColor={RoutexTokens.sidebarIcon}
            // A barra lateral e marrom escura nos dois esquemas — e a pele da marca, nao do tema.
            sideBarBackgroundDarkColor={RoutexTokens.espresso950}
            sideBarBackgroundLightColor={RoutexTokens.espresso950}
            sideBarBackgroundImage={RoutexTokens.sidebarGradient}
            sideBarItemBorderRadius={11}
            sideBarItemHorizontalGap={10}
            sideBarItemActiveBackground={RoutexTokens.sidebarItemActive}
            // Valor unico, nao par claro/escuro — quem escolhe e o colorScheme.
            mainBackground={
              colorScheme === 'dark' ? RoutexTokens.mainBackgroundDark : RoutexTokens.mainBackground
            }
            showHeader={isMedium}
            onCollapsedSideBar={setCollapsed}
            onHiddenSidebar={setHidden}
            navigationData={navigationData}
            sideBarWidth="280px"
            sideBarCollapsedWidth="60px"
            navigationRootLink="/"
            menuItemHeight={isCollapsed ? 44 : 34}
            sideBarFooterHeight={isMedium ? 96 : 110}
            sideBarHeaderHeight={isMedium ? 0 : 54}
            enableSecurity={true}
            sidebarVariant={sidebarVariant}
            showCollapsedButton={sidebarVariant !== 'minimal'}
            sideBarHeaderContent={
              !isMedium && (
                <Group
                  py={4}
                  pr={isCollapsed ? 0 : 16}
                  bg="rgba(0,0,0,.18)"
                  wrap="nowrap"
                  gap={8}
                  justify={isCollapsed ? 'center' : 'space-between'}
                >
                  {/* Aqui o simbolo fica nas cores originais: a barra e marrom escura, e o ambar
                      tem contraste de sobra. O nome vem como texto porque o arquivo do logo e
                      so o simbolo, sem wordmark. Recolhida, so o simbolo — 'RX' era um
                      substituto de quando nao havia logo. */}
                  <Group gap={8} wrap="nowrap" ml={isCollapsed ? 0 : 10}>
                    <Image
                      src={logo.mark}
                      alt="RouteX"
                      // 40px, nao 30: o PNG tem ~15% de margem transparente em cada lado, entao
                      // o simbolo desenhado ocupa 70% da caixa. Em 30 ele saia com ~21px e ficava
                      // apagado ao lado do wordmark branco. Medido, nao estimado.
                      h={isCollapsed ? 34 : 40}
                      w="auto"
                      fit="contain"
                    />
                    {!isCollapsed && (
                      <Text fw={700} size="lg" c="white">
                        RouteX.dev
                      </Text>
                    )}
                  </Group>
                  {!isCollapsed && <CommandPaletteButton navigationData={navigationData} />}
                </Group>
              )
            }
            sideBarFooterContent={
              <Stack gap={0}>
                {/* Card do usuario: borda ambar translucida sobre fundo escurecido, para
                    destacar do gradiente da barra sem virar outro bloco chapado. Mesmo
                    tratamento do vendax-promoter-admin, com o acento do RouteX. */}
                <Flex
                  mx={isCompact ? 6 : 12}
                  mb={8}
                  px={isCompact ? 6 : 12}
                  py={10}
                  align="center"
                  justify={isCompact ? 'center' : 'space-between'}
                  style={{
                    border: '1px solid rgba(245,158,11,.22)',
                    backgroundColor: 'rgba(0,0,0,.11)',
                    borderRadius: 13,
                  }}
                >
                  <Menu shadow="md" width={220} position="top-end" withArrow arrowPosition="center" offset={5}>
                    <Menu.Target>
                      <Group gap={12} style={{ cursor: 'pointer' }} justify={isCompact ? 'center' : 'flex-start'}>
                        <Indicator
                          inline
                          label="A"
                          position="bottom-end"
                          disabled={!user?.isAdmin}
                          styles={{
                            indicator: {
                              display: 'flex',
                              border: '1px solid yellow',
                              justifyContent: 'center',
                              alignItems: 'center',
                              bottom: 5,
                              right: 5,
                              width: '16px',
                              height: '16px',
                              position: 'absolute',
                              zIndex: 400,
                              borderRadius: 50,
                              backgroundColor: 'green',
                            },
                          }}
                          size={18}
                          color="green"
                          offset={7}
                        >
                          <Avatar
                            style={{ cursor: 'pointer' }}
                            radius="xl"
                            src={user ? user.photo || defaultAvatar : defaultAvatar}
                            alt={user ? user.displayName : ''}
                          />
                        </Indicator>
                        {!isCompact && (
                          <Stack gap={0}>
                            <Text c="white" size="sm" fw={500} lineClamp={1}>
                              {user.displayName}
                            </Text>
                            {isDev && (
                              <Badge size="xs" variant="light" color="blue">
                                DEV
                              </Badge>
                            )}
                          </Stack>
                        )}
                      </Group>
                    </Menu.Target>

                    <Menu.Dropdown>
                      <Menu.Label>Usuário</Menu.Label>
                      <Menu.Item leftSection={<IconUserCircle size={14} />} onClick={handleOpenMyProfileModal}>
                        Meu Perfil
                      </Menu.Item>
                      <Menu.Label>Opções</Menu.Label>
                      <Menu.Item
                        leftSection={colorScheme === 'dark' ? <IconSun size={14} /> : <IconMoonStars size={14} />}
                        onClick={toggleColorScheme}
                      >
                        {archbaseI18next.t('archbase:toggleColorScheme')}
                      </Menu.Item>
                      <Menu.Item leftSection={<IconArrowsMaximize size={14} />} onClick={toggle}>
                        Tela cheia
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Label>Layout do Menu</Menu.Label>
                      <Menu.Item
                        leftSection={<IconLayoutSidebar size={14} />}
                        onClick={() => setSidebarVariant('standard')}
                        bg={sidebarVariant === 'standard' ? theme.colors.appPrimary[1] : undefined}
                      >
                        Padrão
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconLayoutSidebarRight size={14} />}
                        onClick={() => setSidebarVariant('rail')}
                        bg={sidebarVariant === 'rail' ? theme.colors.appPrimary[1] : undefined}
                      >
                        Rail (Grupos)
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconLayoutList size={14} />}
                        onClick={() => setSidebarVariant('minimal')}
                        bg={sidebarVariant === 'minimal' ? theme.colors.appPrimary[1] : undefined}
                      >
                        Minimal (Ícones)
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Label>Conta</Menu.Label>
                      <Menu.Item leftSection={<IconBrandMessenger size={14} />}>Suporte</Menu.Item>
                      <Menu.Item color="red" leftSection={<IconLogout size={14} />} onClick={handleLogout}>
                        Sair
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Flex>

                <Flex
                  justify={isCompact ? 'center' : 'space-between'}
                  align="center"
                  px={isCompact ? 4 : 8}
                  style={{
                    backgroundColor: 'rgba(0,0,0,.14)',
                    borderTop: '1px solid rgba(245,158,11,.14)',
                  }}
                  h={48}
                >
                  {!isCompact || isHidden ? (
                    <Text size="sm" c="white" fw={500}>
                      {APP_NAME}
                    </Text>
                  ) : null}
                  <Badge size="sm" variant="light" color="appAccent">
                    {isCompact ? APP_VERSION : `v${APP_VERSION}`}
                  </Badge>
                </Flex>
              </Stack>
            }
            header={
              <ArchbaseAdminLayoutHeader
                user={user}
                headerActions={headerActions()}
                navigationData={navigationData}
                showLanguageSelector={true}
                logo={logo.mark}
                userMenuItems={
                  <Fragment>
                    <Menu.Label>Usuário</Menu.Label>
                    <Menu.Item leftSection={<IconUserCircle size={14} />} onClick={handleOpenMyProfileModal}>
                      Meu Perfil
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Label>Conta</Menu.Label>
                    <Menu.Item leftSection={<IconBrandMessenger size={14} />}>Suporte</Menu.Item>
                    <Menu.Item color="red" leftSection={<IconLogout size={14} />} onClick={handleLogout}>
                      Sair
                    </Menu.Item>
                  </Fragment>
                }
              />
            }
          >
            <ArchbaseAdminTabContainer
              onChangeActiveTabId={(id) => adminStore.setActiveTabId(id)}
              onChangeOpenedTabs={(tabs: ArchbaseTabItem[]) => adminStore.setOpenedTabs(tabs)}
              openedTabs={adminStore.openedTabs}
              activeTabId={adminStore.activeTabId}
              navigationData={navigationData}
            />
          </ArchbaseAdminMainLayout>
          {user && (
            <ArchbaseMyProfileModal
              opened={showMyProfile}
              handleClose={handleCloseMyProfileModal}
              userId={user.id}
              updateUser={handleUpdateUser}
              options={{
                showNickname: false,
                avatarMaxSizeKB: 100,
              }}
            />
          )}
        </Fragment>
      ) : (
        loginView
      )}
    </ArchbaseErrorBoundary>
  )
}

function App() {
  const [colorScheme, setColorScheme] = useLocalStorage<'light' | 'dark'>({
    key: 'mantine-color-scheme',
    defaultValue: 'light',
    getInitialValueInEffect: true,
  })
  const [dark, setThemeDark] = useState<MantineThemeOverride>(AppThemeDark)
  const [light, setThemeLight] = useState<MantineThemeOverride>(AppThemeLight)
  const [currentUser, setCurrentUser] = useState<AppUser | undefined>(undefined)

  useEffect(() => {
    if (currentUser) {
      persistCurrentUser(currentUser)
      return
    }
    clearCurrentUser()
  }, [currentUser])

  const handleChangeCustomTheme = (dark: MantineThemeOverride, light: MantineThemeOverride) => {
    setThemeDark(dark)
    setThemeLight(light)
  }

  const toggleColorScheme = (value?: 'light' | 'dark') =>
    setColorScheme(value || (colorScheme === 'dark' ? 'light' : 'dark'))

  useHotkeys([['mod+J', () => toggleColorScheme()]])

  const handleLoginUser = (user: AppUser) => {
    setCurrentUser(user)
    persistCurrentUser(user)
  }

  const handleLogoutUser = () => {
    setCurrentUser(undefined)
    clearCurrentUser()
  }

  const securityUser = currentUser
    ? {
        id: currentUser.id,
        name: currentUser.displayName,
        email: currentUser.email,
        isAdministrator: currentUser.isAdmin || false,
        code: '',
        version: 0,
        createEntityDate: '',
        updateEntityDate: '',
        createdByUser: '',
        lastModifiedByUser: '',
        description: '',
        actions: [],
        userName: currentUser.email,
        password: '',
        changePasswordOnNextLogin: false,
        allowPasswordChange: true,
        allowMultipleLogins: true,
        passwordNeverExpires: true,
        accountDeactivated: false,
        accountLocked: false,
        unlimitedAccessHours: true,
        groups: [],
        type: 'user',
        isNewUser: false,
        nickname: currentUser.displayName,
      }
    : null

  return (
    <ArchbaseGlobalProvider
      colorScheme={colorScheme}
      containerIOC={containerIOC}
      themeDark={dark}
      themeLight={light}
      translationName={TRANSLATION_NAME}
      translationResource={{
        en: translation_en,
        'pt-BR': translation_ptbr,
        es: translation_es,
      }}
    >
      <ArchbaseSecurityProvider user={securityUser}>
        <ArchbaseAppProvider
          user={currentUser}
          owner={null}
          selectedCompany={undefined}
          variant="filled"
          setCustomTheme={handleChangeCustomTheme}
          navigationProvider={ArchbaseNavigationProvider}
        >
          <Main
            onLoginUser={handleLoginUser}
            onLogoutUser={handleLogoutUser}
            user={currentUser}
            setUser={setCurrentUser}
          />
        </ArchbaseAppProvider>
      </ArchbaseSecurityProvider>
    </ArchbaseGlobalProvider>
  )
}

export default App
